import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Star } from "lucide-react";
import { RevealLines, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { BookingWidget } from "@/components/BookingWidget";
import { IMG, ROOMS, TESTIMONIALS, FACILITIES } from "@/data/site";

const chapters = [
  { n: "01", title: "Rest", text: "Two room categories, one promise — sleep that restores. From the Standard Room to the Superior Room, every space is layered in island calm.", to: "/rooms", label: "Explore Rooms" },
  { n: "02", title: "Taste", text: "Swahili coastal flavours meet contemporary technique in our restaurant, bar and signature breakfast experience.", to: "/restaurant", label: "Discover Dining" },
  { n: "03", title: "Unwind", text: "An ocean-front pool, restorative spa rituals and the Baobab Bar pouring from morning till late.", to: "/facilities/swimming-pool", label: "Explore Facilities" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden flex items-end">
        <motion.div style={{ y: yImg }} className="absolute inset-0">
          <img src={IMG.heroMain} alt="Zuri Sana Hotel" className="w-full h-[115%] object-cover hero-zoom" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/85 via-black/15 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="text-xs md:text-sm uppercase tracking-[0.45em] text-[#d4af37] mb-6"
          >
            Zuri Sana Hotel — Tanzania
          </motion.p>
          <h1 className="font-serif-display font-light text-white text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight max-w-4xl">
            <RevealLines delay={0.3} lines={["A haven of", "golden calm."]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-base md:text-lg text-white/70 max-w-xl"
          >
            Where warm Swahili hospitality meets contemporary luxury — rooms, dining and celebrations crafted around you.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/book" className="btn-gold px-8 py-4 text-[12px] tracking-[0.2em] uppercase font-semibold" data-testid="hero-book-btn">
              Book Your Stay
            </Link>
            <Link to="/rooms" className="btn-outline-gold px-8 py-4 text-[12px] tracking-[0.2em] uppercase" data-testid="hero-rooms-btn">
              Explore Rooms
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={["African Luxury", "World-Class Hospitality", "Zanzibar · Tanzania", "Signature Experiences", "Karibu Sana"]} />

      {/* MANIFESTO CHAPTERS */}
      <section className="py-24 md:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] mb-4">The Zuri Sana Way</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white max-w-3xl leading-tight">
              Three chapters of <em className="gold-text not-italic font-medium">every stay</em>
            </h2>
          </FadeUp>
          <div className="mt-16 md:mt-24 space-y-0">
            {chapters.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.08}>
                <div className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 border-t hairline items-start">
                  <span className="md:col-span-2 font-serif-display text-6xl md:text-7xl font-light text-white/15 group-hover:text-[#d4af37]/60 transition-colors duration-500">{c.n}</span>
                  <h3 className="md:col-span-3 font-serif-display text-3xl md:text-4xl font-light text-white">{c.title}</h3>
                  <p className="md:col-span-5 text-[#a3a3a3] leading-relaxed">{c.text}</p>
                  <Link to={c.to} className="md:col-span-2 inline-flex items-center gap-2 text-[#d4af37] text-sm uppercase tracking-[0.15em] hover:gap-4 transition-[gap] duration-300" data-testid={`chapter-link-${c.n}`}>
                    {c.label} <ArrowRight size={16} />
                  </Link>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ROOMS SHOWCASE */}
      <section className="py-24 md:py-32 bg-[#0d0d0d] border-y hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] mb-4">Stay With Us</p>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white">Accommodation</h2>
            </FadeUp>
            <FadeUp delay={0.15}>
              <Link to="/rooms" className="btn-outline-gold px-6 py-3 text-[12px] tracking-[0.18em] uppercase inline-block" data-testid="home-all-rooms-btn">All Rooms</Link>
            </FadeUp>
          </div>
          <Stagger className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {ROOMS.map((room, i) => (
              <StaggerItem key={room.slug} className={i === 0 ? "md:col-span-7" : "md:col-span-5"}>
                <Link to={`/rooms/${room.slug}`} className="group block" data-testid={`home-room-card-${room.slug}`}>
                  <div className="img-zoom relative aspect-[4/3]">
                    <img src={room.image} alt={room.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <div>
                        <h3 className="font-serif-display text-2xl md:text-3xl font-light text-white">{room.name}</h3>
                        <p className="text-white/60 text-sm mt-1">{room.occupancy} · {room.bed}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* DINING SPOTLIGHT */}
      <section className="py-24 md:py-40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <FadeUp className="img-zoom relative aspect-[4/5]">
            <img src={IMG.restaurantPavilion} alt="Beach restaurant at Zuri Sana" className="w-full h-full object-cover" />
            <div className="absolute -bottom-1 -right-1 bg-[#0a0a0a] px-6 py-4 border hairline hidden md:block">
              <p className="font-serif-display italic text-xl text-[#d4af37]">Swahili coast, on a plate.</p>
            </div>
          </FadeUp>
          <div>
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] mb-4">Dining</p>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">An evening measured in <em className="gold-text not-italic">courses</em></h2>
              <p className="mt-6 text-[#a3a3a3] leading-relaxed max-w-lg">
                From the signature breakfast buffet to candlelit dinners and a lounge pouring East Africa's finest, dining at Zuri Sana is an event in itself.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/restaurant" className="btn-gold px-7 py-3.5 text-[12px] tracking-[0.18em] uppercase font-semibold" data-testid="home-restaurant-btn">The Restaurant</Link>
                <Link to="/bar-lounge" className="btn-outline-gold px-7 py-3.5 text-[12px] tracking-[0.18em] uppercase" data-testid="home-bar-btn">Baobab Bar</Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* FACILITIES STRIP */}
      <section className="py-24 md:py-32 bg-[#0d0d0d] border-y hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] mb-4">Facilities</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light text-white mb-16">Everything, thoughtfully placed</h2>
          </FadeUp>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FACILITIES.slice(0, 4).map((f) => (
              <StaggerItem key={f.slug}>
                <Link to={f.to || `/facilities/${f.slug}`} className="group block" data-testid={`home-facility-${f.slug}`}>
                  <div className="img-zoom aspect-[3/4]">
                    <img src={f.image} alt={f.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="mt-4 font-serif-display text-xl text-white group-hover:text-[#d4af37] transition-colors duration-300">{f.name}</h3>
                  <p className="text-sm text-white/50 mt-1">{f.short}</p>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24 md:py-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <FadeUp>
            <div className="flex justify-center gap-1 mb-8">
              {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-[#d4af37] text-[#d4af37]" />)}
            </div>
            <blockquote className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-light italic text-white/90 leading-snug">
              "{TESTIMONIALS[0].text}"
            </blockquote>
            <p className="mt-8 text-sm uppercase tracking-[0.25em] text-[#d4af37]">{TESTIMONIALS[0].name} — {TESTIMONIALS[0].origin}</p>
            <Link to="/testimonials" className="mt-8 inline-flex items-center gap-2 text-white/50 hover:text-[#d4af37] text-sm transition-colors duration-300" data-testid="home-testimonials-link">
              Read more guest stories <ArrowRight size={15} />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* BOOKING */}
      <section className="pb-24 md:pb-40">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <FadeUp><BookingWidget /></FadeUp>
        </div>
      </section>
    </div>
  );
}
